import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { RecipeCard } from '../components/RecipeCard';
import { Button } from '../components/ui/button';
import { Heart, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Favorites = () => {
  const { getLocalizedText } = useLanguage();
  const { token, isAuthenticated, loading: userLoading } = useUser();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const texts = {
    title: { fi: 'Suosikkini', fr: 'Mes favoris', en: 'My Favorites' },
    noFavorites: { fi: 'Ei vielä suosikkeja', fr: 'Pas encore de favoris', en: 'No favorites yet' },
    browseRecipes: { fi: 'Selaa reseptejä', fr: 'Parcourir les recettes', en: 'Browse recipes' },
    loginRequired: { fi: 'Kirjaudu sisään nähdäksesi suosikkisi', fr: 'Connectez-vous pour voir vos favoris', en: 'Sign in to see your favorites' },
    signIn: { fi: 'Kirjaudu', fr: 'Se connecter', en: 'Sign In' }
  };

  useEffect(() => {
    if (!userLoading) {
      if (isAuthenticated) {
        fetchFavorites();
      } else {
        setLoading(false);
      }
    }
  }, [isAuthenticated, userLoading, token]);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  if (userLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen py-20" data-testid="favorites-page">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Heart className="h-16 w-16 mx-auto text-muted mb-4" />
          <h1 className="text-3xl font-bold mb-4">{getLocalizedText(texts.title)}</h1>
          <p className="text-foreground/60 mb-8">{getLocalizedText(texts.loginRequired)}</p>
          <Link to="/auth">
            <Button className="rounded-full" data-testid="login-btn">
              {getLocalizedText(texts.signIn)}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="favorites-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/recipes">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Heart className="h-8 w-8 text-secondary fill-secondary" />
            {getLocalizedText(texts.title)}
          </h1>
        </div>

        {favorites.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {favorites.map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-card rounded-2xl border border-border">
            <Heart className="h-16 w-16 mx-auto text-muted mb-4" />
            <p className="text-foreground/60 text-lg mb-4">
              {getLocalizedText(texts.noFavorites)}
            </p>
            <Link to="/recipes">
              <Button className="rounded-full" data-testid="browse-recipes-btn">
                {getLocalizedText(texts.browseRecipes)}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
